import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppIcon from '../common/AppIcon';

interface SuggestedTopic {
  name: string;
  emoji: string | null;
  tasks: string[];
}

interface TaskSelectionStepProps {
  suggestions: SuggestedTopic[];
  selectedTopics: Set<string>;
  selectedTasks: Map<string, Set<string>>;
  onChange: (tasks: Map<string, Set<string>>) => void;
  mobileTopicIndex: number;
}

const TaskSelectionStep: React.FC<TaskSelectionStepProps> = ({
  suggestions,
  selectedTopics,
  selectedTasks,
  onChange,
  mobileTopicIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const activeTopics = suggestions.filter((t) => selectedTopics.has(t.name));

  const toggleTask = (topicName: string, taskTitle: string) => {
    const next = new Map(selectedTasks);
    const topicTasks = new Set(next.get(topicName) || []);
    if (topicTasks.has(taskTitle)) {
      topicTasks.delete(taskTitle);
    } else {
      topicTasks.add(taskTitle);
    }
    next.set(topicName, topicTasks);
    onChange(next);
  };

  const toggleAllForTopic = (topicName: string, allTasks: string[]) => {
    const next = new Map(selectedTasks);
    const current = next.get(topicName) || new Set<string>();
    if (current.size === allTasks.length) {
      next.set(topicName, new Set());
    } else {
      next.set(topicName, new Set(allTasks));
    }
    onChange(next);
  };

  const renderTopicTasks = (topic: SuggestedTopic) => {
    const topicTasks = selectedTasks.get(topic.name) || new Set<string>();
    const allChecked = topicTasks.size === topic.tasks.length;
    const someChecked = topicTasks.size > 0 && !allChecked;

    return (
      <Box key={topic.name}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={() => toggleAllForTopic(topic.name, topic.tasks)}
              sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Select all ({topicTasks.size}/{topic.tasks.length})
            </Typography>
          }
        />
        <Box sx={{ pl: 1 }}>
          {topic.tasks.map((task) => (
            <FormControlLabel
              key={task}
              control={
                <Checkbox
                  checked={topicTasks.has(task)}
                  onChange={() => toggleTask(topic.name, task)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{task}</Typography>}
              sx={{ display: 'flex', ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Mobile: show one topic at a time
  if (isMobile) {
    const currentTopic = activeTopics[mobileTopicIndex];
    if (!currentTopic) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Topic {mobileTopicIndex + 1} of {activeTopics.length}
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}
          >
            {currentTopic.emoji} {currentTopic.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the tasks you'd like to track. You can always change these later.
          </Typography>
        </Box>
        {renderTopicTasks(currentTopic)}
      </Box>
    );
  }

  // Desktop: all topics with collapsible accordions
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        variant="h6"
        sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600, mb: 1 }}
      >
        Customize your tasks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Review and select the tasks you want for each topic. You can always add, edit, or remove tasks later.
      </Typography>
      {activeTopics.map((topic) => {
        const topicTasks = selectedTasks.get(topic.name) || new Set<string>();
        return (
          <Accordion
            key={topic.name}
            defaultExpanded
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: '8px !important',
              '&::before': { display: 'none' },
              '&:not(:last-child)': { mb: 0.5 },
            }}
          >
            <AccordionSummary expandIcon={<AppIcon name="chevron-down" />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{topic.emoji}</span>
                <Typography sx={{ fontWeight: 600 }}>{topic.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ({topicTasks.size}/{topic.tasks.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {renderTopicTasks(topic)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TaskSelectionStep;
